import { useCallback } from 'react';

import {
  ChakraBox,
  ChakraSimpleGrid,
  DateFormat,
  DatePickerInput,
  DropdownV2,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  TagColor,
  TagDropdown,
  TagProps,
  TextInputV2,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import {
  BILLING_PERIODS_TOP_LEVEL_ID,
  DATE_PLACEHOLDER,
} from '~/lib/constants';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import util from '~/lib/util/util';

import BillingPeriodLayout from './BillingPeriodLayout';
import { SESSION_UNITS } from './types';
import { UseBillingPeriodState } from './useBillingPeriod';

export const authStatusMenuOptions: TagProps[] = [
  {
    color: TagColor.green,
    id: 'Approved',
    value: 'Approved',
  },
  {
    color: TagColor.marigold,
    id: 'Pending',
    value: 'Pending',
  },
  {
    color: TagColor.red,
    id: 'Denied',
    value: 'Denied',
  },
];

const Authorization = ({
  currentAuthorization,
  allAuthorizations,
  isReadOnly,
  showRequiredFieldErrors,
  index,
  billingPeriodState,
}: {
  currentAuthorization: BillingPeriodAuthorization;
  allAuthorizations: BillingPeriodAuthorization[];
  isReadOnly: boolean;
  showRequiredFieldErrors?: boolean;
  index: number;
  billingPeriodState: UseBillingPeriodState;
}) => {
  const {
    authorizationNumber,
    endDate,
    startDate,
    approvedSessions,
    approvedSessionUnits,
    status,
  } = currentAuthorization;
  const { updateBillingPeriodState } = billingPeriodState;

  const handleAuthorizationChange = useCallback(
    (field, value) => {
      const updatedAuthorization = {
        ...currentAuthorization,
        [field]: value,
      };
      updateBillingPeriodState({
        authorizationDetails: allAuthorizations.map((auth, indx) =>
          indx === index ? updatedAuthorization : auth,
        ),
      });
    },
    [currentAuthorization, updateBillingPeriodState, allAuthorizations, index],
  );

  const handleAuthorizationDelete = useCallback(() => {
    const authIndex = allAuthorizations.findIndex(
      (auth) => auth === currentAuthorization,
    );

    if (authIndex !== -1) {
      const updatedAuthorizations = allAuthorizations
        .map((auth, ind) => {
          if (ind === authIndex && auth.externalId) {
            return { ...auth, isDeleted: true };
          }
          if (ind === authIndex && !auth.externalId) {
            return null;
          }
          return auth;
        })
        .filter(Boolean);

      updateBillingPeriodState({
        authorizationDetails: updatedAuthorizations,
      });
    }
  }, [allAuthorizations, currentAuthorization, updateBillingPeriodState]);

  if (currentAuthorization.isDeleted) {
    return null;
  }

  return (
    <BillingPeriodLayout
      action={
        <FireflyButton
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-delete`}
          leftIcon={FAIconName.trashCanRegular}
          onClick={handleAuthorizationDelete}
          variant="secondary"
        >
          Delete
        </FireflyButton>
      }
      isHeaderVisible={!isReadOnly}
      title={`Authorization ${index + 1}`}
    >
      <ChakraSimpleGrid columns={3} spacing="20px" width="100%">
        <InputGroupV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-number`}
          isRequired={isReadOnly || false}
          label="Authorization number"
        >
          <TextInputV2
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-number-input`}
            isReadOnly={isReadOnly}
            onChange={(value) =>
              handleAuthorizationChange('authorizationNumber', value)
            }
            placeholder="Enter a number"
            value={authorizationNumber?.toString() ?? ''}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__sessions-approved`}
          isRequired={isReadOnly || false}
          label="Sessions approved"
        >
          <TextInputV2
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__sessions-approved-input`}
            isReadOnly={isReadOnly}
            onChange={(value) => {
              const sessionsValue = Number(value);
              if (sessionsValue < 0) return;
              handleAuthorizationChange('approvedSessions', sessionsValue);
            }}
            placeholder="Enter a number"
            type={TextInputV2Type.NUMBER}
            value={approvedSessions?.toString() ?? ''}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-session-units`}
          isRequired={isReadOnly || false}
          label="Session units"
        >
          <DropdownV2
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-session-units-dropdown`}
            isInvalid={showRequiredFieldErrors}
            isReadOnly={isReadOnly}
            menuOptions={(SESSION_UNITS ?? []).map(({ label }) => ({
              label,
              value: label,
            }))}
            onOptionClick={(value) =>
              handleAuthorizationChange('approvedSessionUnits', value)
            }
            placeholder="Please choose"
            value={util.capitalize(approvedSessionUnits)}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-startdate`}
          isRequired={isReadOnly || false}
          label="Authorization start date"
        >
          <DatePickerInput
            defaultDate={
              startDate
                ? DateTime.fromFormat(
                  startDate,
                  DateFormat.shortYearMonthDay,
                ).toJSDate()
                : undefined
            }
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-startdate-input`}
            isReadOnly={isReadOnly}
            onSubmit={(date: Date) =>
              handleAuthorizationChange(
                'startDate',
                DateTime.fromJSDate(date).toISODate(),
              )
            }
            placeholder={DATE_PLACEHOLDER}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-enddate`}
          isRequired={isReadOnly || false}
          label="Authorization end date"
        >
          <DatePickerInput
            defaultDate={
              endDate
                ? DateTime.fromFormat(
                  endDate,
                  DateFormat.shortYearMonthDay,
                ).toJSDate()
                : undefined
            }
            id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-enddate-input`}
            isReadOnly={isReadOnly}
            minDate={new Date(startDate)}
            onSubmit={(date: Date) =>
              handleAuthorizationChange(
                'endDate',
                DateTime.fromJSDate(date).toISODate(),
              )
            }
            placeholder={DATE_PLACEHOLDER}
          />
        </InputGroupV2>
        <InputGroupV2
          id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-status`}
          label="Authorization status"
        >
          <ChakraBox justifyContent="flex-start" width={isReadOnly && '70px'}>
            <TagDropdown
              id={`${BILLING_PERIODS_TOP_LEVEL_ID}__auth-status-dropdown`}
              isInvalid={showRequiredFieldErrors}
              isReadOnly={isReadOnly}
              menuOptions={authStatusMenuOptions}
              onOptionClick={(value) =>
                handleAuthorizationChange('status', value)
              }
              value={util.capitalize(status)}
              variant="outline"
            />
          </ChakraBox>
        </InputGroupV2>
      </ChakraSimpleGrid>
    </BillingPeriodLayout>
  );
};

export default Authorization;